import { useEffect, useMemo, useState } from "react";

import Checkbox from "@/components/botanic/Checkbox";
import ExpandableFilterOptions from "@/components/ExpandableFilterOptions";
import FilterSearch from "@/components/Filters/FilterSearch";

import { NoFollowLinkProp } from "../DynamicFilters/DynamicFilters";
import useQueryFilterValues from "../DynamicFilters/hooks/useQueryFilterValues";
import { GroupFilter, GroupFilterValue } from "../DynamicFilters/types";
import LabelWithBadge from "../LabelWithBadge";
import { getUpdatedFilterHref } from "../utils/filterUtils";

const Checkboxes: React.FC<{
  disabled?: (option: GroupFilterValue) => boolean;
  options: GroupFilterValue[];
  showCount?: boolean;
  name: string;
  currentPath: string;
  noFollowLink?: NoFollowLinkProp;
}> = ({
  options,
  name,
  disabled,
  currentPath,
  showCount = false,
  noFollowLink,
}) => {
  const selectedValues: string[] = useQueryFilterValues(name);

  return (
    <>
      {options.map((filter) => {
        const href = getUpdatedFilterHref(currentPath, name, filter.value);
        return (
          <Checkbox
            key={`${name}_${filter.value}`}
            href={href}
            id={`${name}_${filter.value}`}
            data-testid={`${name}_${filter.value}`}
            disabled={disabled?.(filter) || filter.count === 0}
            name={name}
            value={filter.value}
            checked={selectedValues.includes(filter.value)}
            className="p-xs"
            rel={noFollowLink?.(href) ? "nofollow" : undefined}
          >
            <LabelWithBadge
              displayName={filter.label}
              badge={filter?.badge || ""}
            />
            {showCount && (
              <span className="pl-sm text-grey">({filter.count})</span>
            )}
          </Checkbox>
        );
      })}
    </>
  );
};

interface Props {
  filter: GroupFilter;
  disabled?: (option: GroupFilterValue) => boolean;
  currentPath: string;
  optionTake?: number;
  noFollowLink?: NoFollowLinkProp;
}

const CheckboxSearchList = ({
  disabled,
  currentPath,
  filter,
  optionTake = 7,
  noFollowLink,
}: Props) => {
  const [subFiltersResult, setSubFiltersResult] = useState(filter.values);
  const [visibleOptions, hiddenOptions] = useMemo(
    () => [
      subFiltersResult.slice(0, optionTake), // visibleOptions
      subFiltersResult.slice(optionTake), // hiddenOptions
    ],
    [subFiltersResult],
  );

  const hideFilterSearch = filter.values.length <= optionTake;
  const checkboxesProps = {
    currentPath,
    disabled,
    name: filter.name,
    noFollowLink,
    showCount: filter.showCount,
  };

  // * This effect is required in order for the list to update with new server data.
  useEffect(() => {
    setSubFiltersResult(filter.values);
  }, [filter.values]);

  return (
    <>
      {!hideFilterSearch && (
        <FilterSearch
          label={filter.label}
          options={filter.values}
          handleFilterSearch={(subFilters: GroupFilterValue[]) =>
            setSubFiltersResult(subFilters)
          }
          fuseOptions={{ keys: ["value"] }}
        />
      )}
      <div
        className="overflow-auto max-h-80"
        data-testid="filter-checkbox-list"
      >
        <Checkboxes options={visibleOptions} {...checkboxesProps} />

        {Boolean(hiddenOptions.length) && (
          <ExpandableFilterOptions
            expandButtonText={`Show all ${filter.values.length.toLocaleString(
              "en-us",
            )}`}
          >
            <Checkboxes options={hiddenOptions} {...checkboxesProps} />
          </ExpandableFilterOptions>
        )}
      </div>
    </>
  );
};

export default CheckboxSearchList;
